import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetUserQuery, useGetUserWalletMutation, useExportUsersMutation } from "../../../redux/userApi";
import ReactDatatable from "@mkikets/react-datatable";
import { useGetDashboardQuery } from "../../../redux/dashboardApi";
import { toast } from "react-toastify";
import moment from "moment";
import { Button, Dropdown } from "react-bootstrap";
import EditUser from "../../partial/EditUser";
import BlockUser from "../../partial/BlockUser";
import RestrictUser from "../../partial/RestrictUser";
import ViewUserWallets from "../../partial/ViewUserWallets";
import UserTreeModal from "../../partial/UserTreeModal";
import LogoutUserModal from "../../partial/LogoutUserModal";

import { useTranslation } from "react-i18next";

function Users() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: totalUser } = useGetDashboardQuery();

  const [params, setParams] = useState({
    page: 1,
    limit: 5,
    search: ""
  });
  const [searchTerm, setSearchTerm] = useState("");
  const { data: apiResponse } = useGetUserQuery(params);

  const [getUserWallet] = useGetUserWalletMutation();
  const [exportUsers, { isLoading: isExporting }] = useExportUsersMutation();

  const users = apiResponse?.data || [];
  const totalUsers = totalUser?.total_user || 0;

  const [show, setShow] = useState(false)
  const [id, setId] = useState("")

  // Edit Modal State
  const [showEdit, setShowEdit] = useState(false);
  const [editData, setEditData] = useState({});

  // Block Modal State
  const [showBlock, setShowBlock] = useState(false);
  const [blockData, setBlockData] = useState({});

  // Restrict Modal State
  const [showRestrict, setShowRestrict] = useState(false);
  const [restrictData, setRestrictData] = useState({});

  // Wallet Modal State
  const [showWallet, setShowWallet] = useState(false);
  const [walletData, setWalletData] = useState([]);

  // Tree Modal State
  const [showTree, setShowTree] = useState(false);
  const [treeUserId, setTreeUserId] = useState(null);

  // Logout User Modal State
  const [showLogout, setShowLogout] = useState(false);
  const [logoutUserData, setLogoutUserData] = useState(null);

  // Logout All Users Modal State
  const [showLogoutAll, setShowLogoutAll] = useState(false);

  const handleEdit = (user) => {
    setEditData(user);
    setShowEdit(true);
  };

  const handleBlock = (user) => {
    setBlockData(user);
    setShowBlock(true);
  };

  const handleRestrict = (user) => {
    setRestrictData(user);
    setShowRestrict(true);
  };

  const handleViewWallet = async (userId) => {
    const response = await getUserWallet({ user_id: userId });
    if (response.data) {
      setWalletData(response.data.data || []);
      setShowWallet(true);
    }
  };

  const handleTree = (userId) => {
    setTreeUserId(userId);
    setShowTree(true);
  };

  const handleLogoutUser = (user) => {
    setLogoutUserData(user);
    setShowLogout(true);
  };

  const handleLogoutAll = () => {
    setShowLogoutAll(true);
  };

  const handleExportUsers = async () => {
    const response = await exportUsers();

    // Check if the response was successful

    if (response?.data?.status) {
      const usersData = response.data.data;
      if (usersData && usersData.length > 0) {
        const customColumns = [
          { label: t('sr_no') || "Sr. No.", key: "sr_no" },
          { label: t('member_name') || "Member Name", key: "member_name" },
          { label: t('email') || "Email", key: "email" },
          { label: t('phone_number') || "Phone Number", key: "mobile" },
          { label: t('recommendation_code') || "Recommendation Code (Sponsor Code)", key: "SponserCode" },
          { label: t('direct_referral_count') || "Recommendation Direct Recommendation (Direct Referral Count)", key: "direct_referral_count" },
          { label: t('acceptance_points') || "Acceptance Points (Total Shopping Points)", key: "total_shopping_points" },
          { label: t('total_points_quantity') || "Total Points Quantity", key: "admob_points_total" },
          { label: t('lower_level_members_count') || "Lower-level Members (Child Member Count)", key: "child_member_count" },
          { label: t('register_date') || "Register Date", key: "created_at" },
          { label: t('last_login_at') || "Last Login At", key: "last_login_at" },
          { label: t('evm_wallet') || "EVM Wallet", key: "evm_wallet" },
          { label: t('tron_wallet') || "Tron Wallet", key: "tron_wallet" },
          { label: t('solana_wallet') || "Solana Wallet", key: "solana_wallet" },
        ];

        const csvRows = usersData.map((row, index) => {
          let wallets = [];
          if (Array.isArray(row.wallets)) {
            wallets = row.wallets;
          } else if (typeof row.wallets === "string") {
            try {
              const parsed = JSON.parse(row.wallets);
              if (Array.isArray(parsed)) wallets = parsed;
            } catch (e) {
              wallets = [];
            }
          }

          return customColumns.map(col => {
            let value = row[col.key];

            if (col.key === "sr_no") {
              value = index + 1;
            } else if (col.key === "member_name") {
              const fullName = [row.fname, row.lname].filter(Boolean).join(" ");
              value = fullName || row.member_name || "";
            } else if (col.key === "email") {
              value = row.email || "";
            } else if (col.key === "mobile") {
              const mobile = String(row.mobile ?? "");
              value = mobile ? (mobile.startsWith("820") ? "82" + mobile.slice(3) : mobile) : "";
            } else if (col.key === "SponserCode") {
              value = row.SponserCode || row.SponsorCode || row.sponsor_code || "";
            } else if (col.key === "direct_referral_count") {
              value = row.direct_referral_count ?? 0;
            } else if (col.key === "total_shopping_points") {
              value = row.total_shopping_points ?? 0;
            } else if (col.key === "admob_points_total") {
              value = row.admob_points_total ?? 0;
            } else if (col.key === "child_member_count") {
              value = row.child_member_count ?? 0;
            } else if (col.key === "created_at" && row.created_at) {
              value = moment(row.created_at).format("DD/MM/YYYY HH:mm:ss");
            } else if (col.key === "last_login_at" && row.last_login_at) {
              value = moment(row.last_login_at).format("DD/MM/YYYY HH:mm:ss");
            } else if (col.key === "evm_wallet") {
              value = wallets.find((item) => item?.coin_name === "EVM")?.wallet_address || "";
            } else if (col.key === "tron_wallet") {
              value = wallets.find((item) => item?.coin_name === "TRX")?.wallet_address || "";
            } else if (col.key === "solana_wallet") {
              value = wallets.find((item) => item?.coin_name === "SOL")?.wallet_address || "";
            }

            const strValue = String(value ?? "");
            if (strValue.includes(',') || strValue.includes('"') || strValue.includes('\n')) {
              return `"${strValue.replace(/"/g, '""')}"`;
            }
            return strValue;
          }).join(',');
        });

        const csvString = [customColumns.map(col => col.label).join(','), ...csvRows].join('\n');

        const blob = new Blob(["\uFEFF" + csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        if (link.download !== undefined) {
          const url = URL.createObjectURL(blob);
          link.setAttribute("href", url);
          link.setAttribute("download", `Users_Export_${moment().format("YYYYMMDD_HHmmss")}.csv`);
          link.style.visibility = 'hidden';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        toast.info(t('no_data_to_export') || "No data available to export.");
      }
      toast.success(response.data.message || t('export_success'));
    } else if (response?.error) {
      toast.error(response.error?.data?.message || t('export_failed'));
    } else {
      toast.error(response?.data?.message || t('export_failed'));
    }
  };

  const columns = [
    {
      key: "srno.",
      text: t('sr_no'),
      className: "sr_no.",
      align: "left",
      sortable: true,
      cell: (row, index) => (params.page - 1) * params.limit + index + 1,
    },
    {
      key: "fname",
      text: t('first_name'),
      className: "name",
      align: "left",
      sortable: true,
    },
    {
      key: "lname",
      text: t('last_name'),
      className: "name",
      align: "left",
      sortable: true,
    },
    {
      key: "email",
      text: t('email'),
      className: "email",
      align: "left",
      sortable: true,
    },
    {
      key: "countryCode",
      text: t('country_code'),
      className: "mobile_number",
      align: "left",
      sortable: true,
    },
    {
      key: "mobile",
      text: t('mobile_number'),
      className: "mobile_number",
      align: "left",
      sortable: true,
      cell: (record) => {
        const mobile = record.mobile;
        return <>{mobile ? mobile.startsWith("820") ? "82" + mobile.slice(3) : mobile : ""}</>;
      },
    },
    {
      key: "status",
      text: t('status'),
      className: "status",
      align: "left",
      sortable: true,
    },
    {
      key: "created_at",
      text: t('register_date'),
      className: "email",
      align: "left",
      sortable: true,
      cell: (record) => {
        const date = record.created_at;
        return <>{moment(date).format("DD/MM/YYYY HH:mm:ss")}</>;
      },
    },
    {
      key: "action",
      text: t('actions'),
      className: "Action d-flex",
      align: "left",
      sortable: true,
      cell: (record) => {
        return (
          <Dropdown>
            <Dropdown.Toggle variant="primary" size="sm" id={`dropdown-${record.id}`}>
              {t('actions') || 'Actions'}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleTree(record.id)}>
                {t('tree') || 'Tree'}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleViewWallet(record.id)}>
                {t('wallets')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleEdit(record)}>
                {t('edit')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleBlock(record)}>
                {record.status === "active" ? t('block') : t('unblock')}
              </Dropdown.Item>
              <Dropdown.Item 
                onClick={() => record.is_restrict === "no" && handleRestrict(record)} 
                disabled={record.is_restrict !== "no"}
              >
                {record.is_restrict === "no" ? (t('restrict') || 'Restrict') : (t('restricted') || 'Restricted')}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => navigate(`/user-daily-bonus?user_id=${record.id}`)}>
                {t('daily_bonus_history') || 'Daily Bonus History'}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => navigate(`/user-watch-points?user_id=${record.id}`)}>
                {t('watch_points_history') || 'Watch Points History'}
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleLogoutUser(record)}>
                {t('logout_user') || 'Logout'}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ];

  const config = {
    page_size: 5,
    length_menu: [5, 10, 20, 50],
    filename: "Fund Request List",
    no_data_text: t('no_record_found'),
    button: {
      // print: true,
      // csv: true,
    },
    language: {
      filter: t('search_in_records'),

      pagination: {
        first: t('first'),
        previous: t('previous'),
        next: t('next'),
        last: t('last'),
      },
    },
    show_length_menu: true,
    show_filter: false,
    show_pagination: true,
    show_info: true,
  };

  const handleShow = (value) => {
    setId(value)
    setShow(true)
  }

  const handleTableChange = (newParams) => {
    setParams({
      ...params,
      page: newParams.page_number,
      limit: newParams.page_size,
      filter_value: newParams.filter_value
    });
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  const handleSearchSubmit = () => {
    setParams({
      ...params,
      page: "",
      limit: "",
      search: searchTerm
    });
  }

  return (
    <section className="section dashboard">
      <div className="pagetitle d-flex align-items-center justify-content-between mb-4">
        <h1>{t('users_list')}</h1>
        <div className="col-md-6 d-flex align-items-center gap-2">
          <input
            type="text"
            className="form-control me-2"
            placeholder={t('search_placeholder')}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <button className="btn btn-primary" onClick={handleSearchSubmit}>{t('search')}</button>
          <Button
            variant="success"
            onClick={handleExportUsers}
            disabled={isExporting}
            className="text-nowrap"
          >
            {isExporting ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                {t('exporting') || 'Exporting...'}
              </>
            ) : (
              t('export_users') || 'Export Users'
            )}
          </Button>
          <Button
            variant="danger"
            onClick={handleLogoutAll}
            className="text-nowrap"
          >
            {t('logout_all_users') || 'Logout All Users'}
          </Button>
        </div>
      </div>

      <div className="card">
        <div className="card-body pt-4">
          <ReactDatatable
            key={`datatable-${params.search}`}
            config={config}
            records={users}
            columns={columns}
            dynamic={true}
            total_record={totalUsers}
            onChange={handleTableChange}
          />

        </div>
      </div>
      {/* <AddAdminBonus setShow={setShow} show={show} id={id} /> */}

      <EditUser
        show={showEdit}
        onHide={() => setShowEdit(false)}
        user={editData}
      />

      <BlockUser
        show={showBlock}
        onHide={() => setShowBlock(false)}
        user={blockData}
      />

      <ViewUserWallets
        show={showWallet}
        onHide={() => setShowWallet(false)}
        walletData={walletData}
      />

      <RestrictUser
        show={showRestrict}
        onHide={() => setShowRestrict(false)}
        user={restrictData}
      />

      <UserTreeModal
        show={showTree}
        onHide={() => setShowTree(false)}
        userId={treeUserId}
      />

      <LogoutUserModal
        show={showLogout}
        onHide={() => setShowLogout(false)}
        user={logoutUserData}
        logoutAll={false}
      />

      <LogoutUserModal
        show={showLogoutAll}
        onHide={() => setShowLogoutAll(false)}
        user={null}
        logoutAll={true}
      />

    </section>
  );
}

export default Users;
